import { apiClient, type ApiResponse } from './apiClient';
import { DEBUG } from '@/config/api-config';

export type PaymentTransactionStatus =
  | 'pending'
  | 'authorized'
  | 'paid'
  | 'refunded'
  | 'cancelled'
  | 'failed';

export interface PaymentTransaction {
  id: string;
  booking_id?: string | null;
  booking_metadata?: string | null;
  payment_method_id?: string;
  transaction_id?: string;
  gateway_order_id?: string;
  gateway_session_id?: string;
  status: PaymentTransactionStatus | string;
  amount: string | number;
  currency?: string;
  gateway_response?: string | null;
  webhook_data?: string | null;
  error_message?: string | null;
  processed_at?: string | null;
  createdAt: string;
  updatedAt: string;
  // Optional denormalized relations for details view
  PaymentMethod?: {
    id: string;
    provider_name: string;
    provider_code: string;
    image_url?: string;
    description?: string;
    status?: string;
    display_order?: number;
    public_key?: string;
    secret_key?: string;
    merchant_id?: string;
    callback_url?: string;
    createdAt?: string;
    updatedAt?: string;
  };
  Booking?: {
    id: string;
    userId: string;
    startDate: string;
    endDate: string;
    status: string;
    payment_status: string;
    couponCode?: string | null;
    originalAmount: number;
    discountAmount: number;
    finalAmount: number;
    property_id: number;
    total_price: string;
    deposit_amount: string;
    deposit_paid: string;
    remaining_amount: string;
    payment_method: string;
    payment_reference?: string | null;
    check_in: string;
    check_out: string;
    guest_count: number;
    special_requests?: string | null;
    notes?: string | null;
    payment_method_id?: string;
    gateway_order_id?: string;
    gateway_session_id?: string;
    gateway_webhook_data?: string | null;
    createdAt: string;
    updatedAt: string;
    couponId?: string | null;
    User?: {
      id: string;
      name: string;
      email: string;
      phone?: string;
    };
  } | null;
  // Legacy fields for backward compatibility
  user_id?: string;
  provider_code?: string;
  method?: string;
  booking?: any;
  user?: any;
  payment_method?: any;
}

export interface PaymentTransactionsFilters {
  provider_code?: string;
  status?: PaymentTransactionStatus | string;
  booking_id?: string;
  user_id?: string;
  transaction_id?: string;
  gateway_order_id?: string;
  gateway_session_id?: string;
  search?: string; // General search across multiple fields
  created_from?: string; // ISO date
  created_to?: string;   // ISO date
  amount_min?: number;
  amount_max?: number;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
}

export interface TransactionsListResponse {
  transactions: PaymentTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface ProviderLog {
  id: string;
  payment_method_id?: string;
  request_type?: string;
  request_url?: string;
  request_body?: string | null;
  response_body?: string | null;
  status_code?: number;
  error_message?: string | null;
  execution_time_ms?: number;
  provider_code?: string;
  level?: 'info' | 'warn' | 'error' | 'debug' | string;
  message?: string;
  context?: Record<string, any>;
  createdAt: string;
}

export interface ProviderLogsFilters {
  provider_code?: string;
  payment_method_id?: string;
  request_type?: string; // GET, POST, etc.
  status_code?: number | string;
  level?: 'info' | 'warn' | 'error' | 'debug' | string;
  created_from?: string;
  created_to?: string;
  search?: string; // General search across URL, request/response body
  page?: number;
  limit?: number;
}

export interface ProviderLogsListResponse {
  logs: ProviderLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface TransactionActionResponse {
  step: string;
  status: string;
  action_required?: string | null;
  payment_state?: string;
  internal_order_state?: string;
  notes?: string | null;
  provider_response?: any;
}

class AdminPaymentsService {
  private baseUrl = '/admin/payments';

  async getTransactions(
    filters: PaymentTransactionsFilters = {}
  ): Promise<ApiResponse<TransactionsListResponse>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      // Always include page and limit, even if they're default values
      if (key === 'page' || key === 'limit') {
        params.append(key, String(value || (key === 'page' ? 1 : 10)));
      } else if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    const url =
      params.toString().length > 0
        ? `${this.baseUrl}/transactions?${params.toString()}`
        : `${this.baseUrl}/transactions`;

    if (DEBUG) console.log('💳 [AdminPayments] GET', url);
    
    try {
      const response = await apiClient.get<any>(url);
      
      if (DEBUG) console.log('💳 [AdminPayments] Response:', response);
      
      if (response.success && response.data) {
        // Handle different response formats
        let transactions: PaymentTransaction[] = [];
        let pagination = {
          page: filters.page || 1,
          limit: filters.limit || 10,
          total: 0,
          pages: 1
        };
        
        // Check if response.data has transactions property
        if ((response.data as any).transactions && Array.isArray((response.data as any).transactions)) {
          transactions = (response.data as any).transactions;
          // Check for pagination in response.data
          if ((response.data as any).pagination) {
            const pag = (response.data as any).pagination;
            pagination = {
              page: pag.page || pag.current_page || filters.page || 1,
              limit: pag.limit || pag.per_page || filters.limit || 10,
              total: pag.total || pag.total_items || 0,
              pages: pag.pages || pag.total_pages || 1
            };
          } else {
            // Calculate pagination from transactions
            pagination.total = transactions.length;
            pagination.pages = Math.ceil(pagination.total / pagination.limit);
          }
        } else if (Array.isArray(response.data)) {
          // Direct array response
          transactions = response.data;
          pagination.total = transactions.length;
          pagination.pages = Math.ceil(pagination.total / pagination.limit);
        } else if ((response.data as any).data) {
          // Nested data structure
          const nestedData = (response.data as any).data;
          if (Array.isArray(nestedData)) {
            transactions = nestedData;
            pagination.total = transactions.length;
            pagination.pages = Math.ceil(pagination.total / pagination.limit);
          } else if (nestedData.transactions) {
            transactions = nestedData.transactions || [];
            if (nestedData.pagination) {
              const pag = nestedData.pagination;
              pagination = {
                page: pag.page || pag.current_page || filters.page || 1,
                limit: pag.limit || pag.per_page || filters.limit || 10,
                total: pag.total || pag.total_items || 0,
                pages: pag.pages || pag.total_pages || 1
              };
            }
          }
        }
        
        const listResponse: TransactionsListResponse = {
          transactions,
          pagination
        };
        
        return {
          success: true,
          data: listResponse,
          message: response.message || 'Transactions retrieved successfully'
        };
      }
      
      return response as ApiResponse<TransactionsListResponse>;
    } catch (error: any) {
      if (DEBUG) console.error('💳 [AdminPayments] Error:', error);
      throw error;
    }
  }

  async getTransaction(id: string): Promise<ApiResponse<PaymentTransaction>> {
    const url = `${this.baseUrl}/transactions/${id}`;
    if (DEBUG) console.log('💳 [AdminPayments] GET', url);
    return apiClient.get<PaymentTransaction>(url);
  }

  async capture(
    id: string,
    payload?: { amount?: number }
  ): Promise<ApiResponse<TransactionActionResponse>> {
    const url = `${this.baseUrl}/transactions/${id}/capture`;
    if (DEBUG) console.log('💳 [AdminPayments] POST', url, payload);
    return apiClient.post<TransactionActionResponse>(url, payload || {});
  }

  async cancel(
    id: string
  ): Promise<ApiResponse<TransactionActionResponse>> {
    const url = `${this.baseUrl}/transactions/${id}/cancel`;
    if (DEBUG) console.log('💳 [AdminPayments] POST', url);
    return apiClient.post<TransactionActionResponse>(url);
  }

  async refund(
    id: string,
    payload: {
      amount: string;
      reference_id: string;
      reason: string;
      items: Array<{
        reference_id: string;
        title?: string;
        description?: string;
        quantity?: number;
        unit_price?: string;
        discount_amount?: string;
        image_url?: string;
        product_url?: string;
        gender?: string;
        category?: string;
        color?: string;
        product_material?: string;
        size_type?: string;
        size?: string;
        brand?: string;
        is_refundable?: boolean;
        barcode?: string;
        ppn?: string;
        seller?: string;
      }>;
    }
  ): Promise<ApiResponse<TransactionActionResponse>> {
    const url = `${this.baseUrl}/transactions/${id}/refund`;
    if (DEBUG) console.log('💳 [AdminPayments] POST', url, payload);
    return apiClient.post<TransactionActionResponse>(url, payload);
  }

  async reconcile(
    id: string
  ): Promise<ApiResponse<TransactionActionResponse>> {
    const url = `${this.baseUrl}/transactions/${id}/reconcile`;
    if (DEBUG) console.log('💳 [AdminPayments] POST', url);
    return apiClient.post<TransactionActionResponse>(url);
  }

  async getProviderLogs(
    filters: ProviderLogsFilters = {}
  ): Promise<ApiResponse<ProviderLogsListResponse>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      // Always include page and limit, even if they're default values
      if (key === 'page' || key === 'limit') {
        params.append(key, String(value || (key === 'page' ? 1 : 10)));
      } else if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    const url =
      params.toString().length > 0
        ? `${this.baseUrl}/logs?${params.toString()}`
        : `${this.baseUrl}/logs`;

    if (DEBUG) console.log('💳 [AdminPayments] GET', url);
    
    try {
      const response = await apiClient.get<any>(url);
      
      if (DEBUG) console.log('💳 [AdminPayments] Logs Response:', response);
      
      if (response.success && response.data) {
        // Handle different response formats
        let logs: ProviderLog[] = [];
        let pagination = {
          page: filters.page || 1,
          limit: filters.limit || 10,
          total: 0,
          pages: 1
        };
        
        // Check if response.data has logs property
        if ((response.data as any).logs && Array.isArray((response.data as any).logs)) {
          logs = (response.data as any).logs;
          const data = response.data as any;
          // Check for pagination in response.data (can be nested or at same level)
          if (data.pagination) {
            const pag = data.pagination;
            pagination = {
              page: pag.page || pag.current_page || filters.page || 1,
              limit: pag.limit || pag.per_page || filters.limit || 10,
              total: pag.total || pag.total_items || 0,
              pages: pag.pages || pag.total_pages || 1
            };
          } else if (data.total !== undefined || data.total_pages !== undefined || data.current_page !== undefined) {
            // Pagination fields at same level as logs
            pagination = {
              page: data.current_page || data.page || filters.page || 1,
              limit: data.limit || data.per_page || filters.limit || 10,
              total: data.total || data.total_items || 0,
              pages: data.total_pages || data.pages || Math.ceil((data.total || 0) / (data.limit || data.per_page || filters.limit || 10))
            };
          } else {
            // Calculate pagination from logs
            pagination.total = logs.length;
            pagination.pages = Math.ceil(pagination.total / pagination.limit);
          }
        } else if (Array.isArray(response.data)) {
          // Direct array response
          logs = response.data;
          pagination.total = logs.length;
          pagination.pages = Math.ceil(pagination.total / pagination.limit);
        } else if ((response.data as any).data) {
          // Nested data structure
          const nestedData = (response.data as any).data;
          if (Array.isArray(nestedData)) {
            logs = nestedData;
            pagination.total = logs.length;
            pagination.pages = Math.ceil(pagination.total / pagination.limit);
          } else if (nestedData.logs) {
            logs = nestedData.logs || [];
            if (nestedData.pagination) {
              const pag = nestedData.pagination;
              pagination = {
                page: pag.page || pag.current_page || filters.page || 1,
                limit: pag.limit || pag.per_page || filters.limit || 10,
                total: pag.total || pag.total_items || 0,
                pages: pag.pages || pag.total_pages || 1
              };
            }
          }
        }
        
        const listResponse: ProviderLogsListResponse = {
          logs,
          pagination
        };
        
        return {
          success: true,
          data: listResponse,
          message: response.message || 'Logs retrieved successfully'
        };
      }
      
      return response as ApiResponse<ProviderLogsListResponse>;
    } catch (error: any) {
      if (DEBUG) console.error('💳 [AdminPayments] Logs Error:', error);
      throw error;
    }
  }
}

export const adminPaymentsService = new AdminPaymentsService();
export default adminPaymentsService;


